import MultipleTagSelector from '$components/MultipleTagSelector/MultipleTagSelector';
import {
  useGetAdditionalTagsByCandidateIdQuery,
  useSaveAdditionalTagMutation,
  useUpdateAdditionalTagMutation,
} from '$store/modules/additional-tags';
import { useGetAdditionalTagsQuery } from '$store/modules/form-info';
import { AdditionalTag } from '$types/candidates';
import { useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '$components/Buttons/Button';

import classes from '../CandidateDetails.module.css';

const AdditionalTags = ({ candidateId }: { candidateId: number }) => {
  const [t] = useTranslation('global');

  const { data: additionalTags } = useGetAdditionalTagsQuery();
  const [saveAdditionalTag] = useSaveAdditionalTagMutation();
  const [updateAdditionalTags] = useUpdateAdditionalTagMutation();
  const { data: infoAdditionalTags } = useGetAdditionalTagsByCandidateIdQuery({
    candidateId,
  });

  const firstRender = useRef(true);

  const {
    setValue,
    handleSubmit,
    reset,
    control,
    formState: { isDirty },
  } = useForm<{
    additionalTags: AdditionalTag[];
  }>({
    defaultValues: { additionalTags: [] },
  });

  const additionalTagsValue = useWatch({
    control,
    name: 'additionalTags',
  });

  const handleNewAdditionalTag = (title: string) => {
    saveAdditionalTag({
      Name: title,
      candidateId: Number(candidateId!),
    });
    const newOption = {
      Id: (additionalTags?.length || 0) + 1,
      title,
    };
    setValue('additionalTags', [...additionalTagsValue, newOption]);
  };

  const submit = (data: { additionalTags: AdditionalTag[] }) => {
    updateAdditionalTags({
      additionalTags: data.additionalTags,
      candidateId: Number(candidateId!),
    });

    reset({
      additionalTags: data.additionalTags,
    });
  };

  useEffect(() => {
    if (
      (infoAdditionalTags?.length || 0) > additionalTagsValue?.length &&
      firstRender.current
    ) {
      setValue('additionalTags', infoAdditionalTags!, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      firstRender.current = false;
      reset({ additionalTags: infoAdditionalTags! });
    }
  }, [
    additionalTagsValue?.length,
    infoAdditionalTags,
    isDirty,
    reset,
    setValue,
  ]);

  return (
    <div className={classes.ReadOnlyContainer} id="section-8">
      <p className={classes.ReadOnlyTitle}>{t('formNames.additionalTags')}</p>
      <form onSubmit={handleSubmit(submit)}>
        <MultipleTagSelector
          selectedTags={additionalTagsValue || []}
          options={additionalTags || []}
          arrayName="additionalTags"
          setValue={setValue}
          secondary
          handleNewOption={handleNewAdditionalTag}
        />
        <div className={classes.formButtonContainer}>
          <div
            style={{
              width: '120px',
            }}
          >
            <Button
              disabled={!isDirty}
              bgColor="#079e88"
              showText
              text={t('common.save')}
              submit
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdditionalTags;
